// Deterministic RNG (§18.4): mulberry32. The state is a uint32 stored on its owner, so `World`
// (`w.rng`) and bot brains (`BotBrain.rng`) both satisfy RngHolder and never share a stream.

export interface RngHolder { rng: number }

const GOLDEN_GAMMA = 0x6d2b79f5;
const UINT32 = 4294967296;

/** Normalizes any seed number to a uint32 RNG state. */
export function seedRng(seed: number): number {
  return seed >>> 0;
}

function advance(state: number): number {
  return (state + GOLDEN_GAMMA) >>> 0;
}

function output(s: number): number {
  let t = Math.imul(s ^ (s >>> 15), s | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / UINT32;
}

/** Pure mulberry32 step: returns [value in [0, 1), newState]. */
export function next(state: number): [number, number] {
  const s = advance(state);
  return [output(s), s];
}

/** Value in [0, 1); advances the holder's state. */
export function rand(h: RngHolder): number {
  h.rng = advance(h.rng);
  return output(h.rng);
}

/** Integer in [0, n). */
export function randInt(h: RngHolder, n: number): number {
  return Math.floor(rand(h) * n);
}

/** Integer in [lo, hi] (inclusive). */
export function randIntRange(h: RngHolder, lo: number, hi: number): number {
  return lo + randInt(h, hi - lo + 1);
}

/** Float in [lo, hi). */
export function randRange(h: RngHolder, lo: number, hi: number): number {
  return lo + rand(h) * (hi - lo);
}

export function chance(h: RngHolder, p: number): boolean {
  return rand(h) < p;
}

export function pick<T>(h: RngHolder, items: readonly T[]): T {
  if (items.length === 0) throw new RangeError('pick() from an empty list');
  return items[randInt(h, items.length)];
}

/** Index chosen with probability proportional to its weight; −1 if all weights are ≤ 0. */
export function weightedIndex(h: RngHolder, weights: readonly number[]): number {
  let total = 0;
  for (const w of weights) if (w > 0) total += w;
  if (total <= 0) return -1;
  let r = rand(h) * total;
  let last = -1;
  for (let i = 0; i < weights.length; i++) {
    if (weights[i] <= 0) continue;
    last = i;
    r -= weights[i];
    if (r < 0) return i;
  }
  return last;
}

/** Fisher–Yates shuffle in place. */
export function shuffle<T>(h: RngHolder, items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = randInt(h, i + 1);
    const tmp = items[i];
    items[i] = items[j];
    items[j] = tmp;
  }
  return items;
}

/** FNV-1a over UTF-16 code units: stable uint32 seeds from strings (profile ids, UTC dates). */
export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
